import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query';
import { ResponseProps } from 'services/services.types';

import { RootState } from 'store';
import { setUserState } from 'store/signup/signupSlice';
import { clearUser, setToken } from 'store/user/userSlice';

import { notifyToast } from 'shared/Toast/Toast';

import packageInfo from '../../package.json';

import { PathName } from 'router/paths';
import { withBasePath } from './basePath';
import { session, SESSION_KEYS } from './storage';
import { clearWorkOSCookies, hasWorkOSOverride, hasWorkOSSession } from './workos';

interface IRes {
  data: { data: { accessToken: string; refreshToken: string } };
  error?: {
    data: {
      errorMessage: number;
      message: string;
    };
    status: number;
  };
}

/**
 * Shared refresh request promises, keyed to the refresh token that started each one.
 *
 * The key is a correctness boundary, not a bookkeeping detail. An unkeyed slot let any caller join
 * whatever renewal happened to be in flight, and the portal replaces one patient with another
 * inside the same document (sign-in-as-patient, deep links), so two sessions genuinely overlap.
 * The joiner then received the OTHER session's tokens with no way to tell — every check it could
 * make is about the token it presented, not the one it got back.
 *
 * Renewals for different tokens run independently. A duplicate request is the correct outcome
 * there: they are requests about different sessions, and the one already in flight must not be
 * displaced either.
 */
const renewalsInFlight = new Map<string, Promise<IRes | null>>();

/**
 * The single entry point for both renewal callers, `baseQueryWithReauth` and
 * `renewAccessTokenQuietly`. Routing only one of them through a keyed slot would leave the other
 * reading the raw shared state, which is the same disclosure wearing a different caller.
 *
 * One entry per refresh token: same-token callers join, different-token callers get independent
 * entries and neither can displace the other.
 */
const joinOrStartRenewal = (key: string, start: () => Promise<IRes | null>) => {
  const inFlight = renewalsInFlight.get(key);

  if (inFlight) {
    return inFlight;
  }

  const started = start();
  renewalsInFlight.set(key, started);

  return started;
};

/**
 * Which session a request belongs to.
 *
 * The refresh token rather than the patient id, for two reasons. A patient id is absent during the
 * window between installing tokens and hydrating `/my-account` — `App.tsx` models that state
 * explicitly — so every patient would share the same empty owner there, and two different patients
 * would compare equal. It also APPEARS mid-request when hydration lands, which would make a request
 * look like it had changed hands when nothing had.
 *
 * The refresh token has neither problem: it is present from the moment a session exists, distinct
 * per session, and unchanged by hydration. It does change on renewal, which is what the lineage
 * chain below is for.
 *
 * The patient id is a FALLBACK, not a preference, and it is there for WorkOS. A WorkOS cookie
 * session has no refresh token at all, so without this the owner was empty and `stillOwned` returned
 * true unconditionally — every guard below was a no-op for that entire authentication mode.
 *
 * KNOWN RESIDUAL: this detects a replacement visible in THIS document's store. It cannot see a
 * WorkOS cookie swapped by another tab, because that changes no Redux state here. Nothing readable
 * from the client identifies the server-side cookie session, so that case needs a server-supplied
 * session id to close and is not covered by this guard.
 */
const sessionTokenOf = (state: RootState) =>
  state.user.refreshToken ||
  state.user.userId ||
  state.user._id ||
  state.signUp?.user?.refreshToken ||
  '';

/**
 * Successor links recorded when a renewal returns, so a rotated token is recognised as the same
 * session rather than a new one. Bounded because it only ever needs to span an in-flight request.
 */
const renewalLineage = new Map<string, string>();
const LINEAGE_LIMIT = 20;

const recordRenewalLineage = (presented: string, returned?: string) => {
  if (!presented || !returned || returned === presented) {
    return;
  }

  renewalLineage.set(presented, returned);

  if (renewalLineage.size > LINEAGE_LIMIT) {
    const oldest = renewalLineage.keys().next().value;

    if (oldest !== undefined) {
      renewalLineage.delete(oldest);
    }
  }
};

/**
 * Whether the session that started a request still owns the store.
 *
 * An unknown owner never matches another unknown owner: two sessions we cannot tell apart must not
 * be treated as the same one just because neither could be identified. Walking the lineage chain is
 * what keeps a legitimate rotation from looking like a replacement.
 */
const ownsSession = (captured: string, live: string) => {
  if (!captured || !live) {
    return false;
  }

  let cursor = captured;

  for (let hops = 0; hops <= LINEAGE_LIMIT; hops += 1) {
    if (cursor === live) {
      return true;
    }

    const next = renewalLineage.get(cursor);

    if (!next) {
      return false;
    }

    cursor = next;
  }

  return false;
};

/** Removes only the entry whose promise settled, so a later renewal for the same key can start. */
const releaseRenewal = (key: string, promise: Promise<IRes | null> | null) => {
  if (promise && renewalsInFlight.get(key) === promise) {
    renewalsInFlight.delete(key);
  }
};

/**
 * Executes a token renewal request using the provided refresh token.
 * @param refreshTokenValue - Refresh token used to obtain a new access token.
 * @param api - RTK Query base query API instance.
 * @param extraOptions - Extra RTK Query options.
 * @returns The refresh response payload or null when no data is returned.
 */
const requestTokenRefresh = async (
  refreshTokenValue: string,
  api: BaseQueryApi,
  extraOptions: {}
): Promise<IRes | null> => {
  const refreshResult = await baseQuery(
    {
      body: { refreshToken: refreshTokenValue },
      headers: {
        'API-KEY': import.meta.env.VITE_API_KEY
      },
      method: 'POST',
      url: '/auth/token-renewal'
    },
    api,
    extraOptions
  );

  if (refreshResult.error) {
    return null;
  }

  return refreshResult as IRes;
};

/**
 * Token renewal over plain fetch, for callers that have no RTK Query `api` to hand.
 * Mirrors the headers `requestTokenRefresh` sends and resolves to the same shape, so the two
 * are interchangeable through the keyed renewal map.
 *
 * "Mirrors" is load-bearing, and the three conditional headers below are the ones that make it
 * true. `requestTokenRefresh` gets its headers from `rawBaseQuery.prepareHeaders`, which adds them
 * on exactly these conditions; sending only the four static ones would make this a *different*
 * request to the same endpoint. The impersonation marker is this portal's audit record of a staff
 * member acting as a patient, and both quiet callers — the AI chat's Convex sign-in and the Zoom chat
 * widget — mount during impersonation, so a renewal either one triggered would reach the backend
 * unattributed. And because both paths share one keyed renewal map, an RTK Query call joining a
 * renewal that failed only for want of a header inherits the failure and takes its `clearUser`
 * path — a background chat renewal signing the patient out.
 */
const renewTokenWithoutApi = async (
  refreshTokenValue: string,
  accessTokenValue?: string
): Promise<IRes | null> => {
  try {
    const staffId = session.get(SESSION_KEYS.STAFF_ID);
    const response = await fetch(`${import.meta.env.VITE_BASE_URL}/auth/token-renewal`, {
      body: JSON.stringify({ refreshToken: refreshTokenValue }),
      credentials: 'include',
      headers: {
        'API-KEY': import.meta.env.VITE_API_KEY ?? '',
        'App-Version': packageInfo.version,
        'Content-Type': 'application/json',
        Platform: 'web-patient',
        ...(staffId ? { 'impersonation-mode-by': staffId } : {}),
        ...(!hasWorkOSSession() && accessTokenValue ? { Authorization: accessTokenValue } : {}),
        ...(hasWorkOSOverride() ? { 'X-WorkOS-Override': 'true' } : {})
      },
      method: 'POST'
    });

    if (!response.ok) {
      return null;
    }

    const tokens = (await response.json())?.data;

    return tokens?.accessToken && tokens?.refreshToken
      ? ({ data: { data: tokens } } as IRes)
      : null;
  } catch {
    return null;
  }
};

/**
 * Renews the access token for a feature that is not the reason the patient is on the page,
 * joining a renewal already in flight when there is one. Returns the new tokens, or null when
 * renewal was not possible. The caller is responsible for dispatching them.
 *
 * Sharing a renewal entry with `baseQueryWithReauth` avoids a duplicate request, and that
 * is all it does. `/auth/token-renewal` returns a fresh pair but does not invalidate the refresh
 * token presented to it — the handler performs no write at all — so two concurrent renewals both
 * succeed and both callers end up holding usable tokens. There is no rotation race to lose here.
 * (That the presented token then stays valid for its full 180 days is a real problem, but a
 * different one: LMD-939.)
 *
 * The cost of sharing is that an RTK Query call which 401s while this renewal is in flight
 * inherits its outcome, including a failure, and that caller's failure path dispatches
 * `clearUser`. Accepted because such a call would be renewing with the same token against the
 * same endpoint, so it was going to fail anyway.
 *
 * Unlike `baseQueryWithReauth`, this never dispatches `clearUser`, never redirects and never
 * toasts. Losing a secondary feature must not end a patient's session or move them off the page.
 */
export const renewAccessTokenQuietly = async (
  refreshTokenValue: string,
  accessTokenValue?: string
): Promise<{ accessToken: string; refreshToken: string } | null> => {
  if (!refreshTokenValue) {
    return null;
  }

  let pendingRefresh: Promise<IRes | null> | null = null;

  try {
    pendingRefresh = joinOrStartRenewal(refreshTokenValue, () =>
      renewTokenWithoutApi(refreshTokenValue, accessTokenValue)
    );

    const renewed = (await pendingRefresh)?.data?.data ?? null;

    // Recorded here, not only in the RTK path. Both quiet callers (the AI chat's Convex sign-in and
    // the Zoom chat widget) dispatch this pair into the store, so an ordinary request already in
    // flight would otherwise find its captured token has no successor, read the same patient as a
    // replacement, and have its valid response converted into a stale-session error.
    recordRenewalLineage(refreshTokenValue, renewed?.refreshToken);

    return renewed;
  } catch {
    return null;
  } finally {
    releaseRenewal(refreshTokenValue, pendingRefresh);
  }
};

/**
 * Returned instead of a response whose session has been replaced. An error rather than the data,
 * because the data belongs to a patient who is no longer signed in here.
 */
const createStaleSessionError = (): { error: FetchBaseQueryError } => ({
  error: {
    data: { errorCode: 1, message: 'Session changed before the response was delivered' },
    error: 'Session changed before the response was delivered',
    status: 'CUSTOM_ERROR'
  }
});

const createEmptyResponseError = (): { error: FetchBaseQueryError } => ({
  error: {
    data: { errorCode: 1, message: 'Empty response body' },
    error: 'Empty response body',
    status: 'CUSTOM_ERROR'
  }
});

/**
 * Raw fetchBaseQuery instance with default headers and URL handling.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_BASE_URL + '/',
  credentials: 'include',
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(`${key}[]`, String(item)));
      } else if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    });
    return searchParams.toString();
  },
  prepareHeaders: (headers, { getState }) => {
    const staffId = session.get(SESSION_KEYS.STAFF_ID);
    headers.set('App-Version', packageInfo.version);
    headers.set('Platform', 'web-patient');
    headers.set('API-KEY', import.meta.env.VITE_API_KEY ?? '');
    staffId && headers.set('impersonation-mode-by', staffId);

    // Skip Authorization header when using WorkOS cookie sessions
    if (!hasWorkOSSession()) {
      const token = headers.get('Authorization') ?? (getState() as RootState).user.accessToken;
      if (token) {
        headers.set('Authorization', token);
      }
    }

    if (hasWorkOSOverride()) {
      headers.set('X-WorkOS-Override', 'true');
    }

    return headers;
  }
});

/**
 * Base query with null-body guard. When the API returns null/empty body,
 * fetchBaseQuery treats it as success ({ data: null }). This wrapper converts
 * that to an error so transformResponse is never called with null (LMD-25490).
 */
export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (!result.error && (result.data === null || result.data === undefined)) {
    const httpStatus = result.meta?.response?.status;
    if (httpStatus === 204 || httpStatus === 205) {
      return result;
    }
    return createEmptyResponseError();
  }
  return result;
};

/**
 * Base RTK Query implementation that refreshes expired tokens and retries once.
 * @param args - RTK Query fetch arguments.
 * @param api - RTK Query base query API instance.
 * @param extraOptions - Extra RTK Query options.
 * @returns API response result with optional re-authentication.
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  /**
   * Bound BEFORE the first await, which is the point of it.
   *
   * Ownership used to be established only after the original request returned, so a request that
   * began under patient A and 401'd after B had signed in read B's session, renewed B, and replayed
   * A's own `args` as B — executing against an account the request was never assembled for.
   */
  const requestOwner = sessionTokenOf(api.getState() as RootState);

  /**
   * Checked after every identity-carrying await, regardless of status. A successful response is as
   * unsafe as a 401 once the session that asked for it has gone: returning it hands one patient's
   * data to the request and cache lifecycle of another, and `.unwrap()` callers consume it in a
   * component context that has since changed.
   *
   * A request that began with no session at all is left alone — public and pre-login calls have no
   * owner to protect.
   */
  const stillOwned = () =>
    !requestOwner || ownsSession(requestOwner, sessionTokenOf(api.getState() as RootState));

  let result = await baseQuery(args, api, extraOptions);

  if (!stillOwned()) {
    return createStaleSessionError();
  }

  const store = api.getState() as RootState;
  const { refreshToken } = store.user;
  const { user } = (api.getState() as RootState).orchestrate;

  const {
    user: { refreshToken: guestUserRefreshToken }
  } = store.signUp;

  if (result.error && result.error.status === 401) {
    // WorkOS cookie-based session: retry once (middleware may have refreshed the cookie)
    if (hasWorkOSSession()) {
      /**
       * A WorkOS session is identified by a cookie, and the cookie is shared across tabs. If another
       * tab replaces it, THIS tab's Redux store is unchanged — so the owner check above compares
       * patient A with patient A and permits a retry that the browser will authenticate as patient B.
       * Nothing readable from the client distinguishes the two; closing that needs a session
       * generation the server can supply and verify (tracked separately — the blind retry itself
       * predates this branch).
       *
       * Until then, do not REPLAY a non-idempotent request. A mutation re-executed under another
       * patient's cookie writes to the wrong account, silently; a refused retry merely surfaces the
       * 401 so the caller can act. That trade is worth making on a PHI surface, and it costs the
       * automatic recovery only for mutations that 401 exactly while the cookie is being refreshed.
       */
      const method = (typeof args === 'string' ? 'GET' : (args.method ?? 'GET')).toUpperCase();

      if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
        return result;
      }

      result = await baseQuery(args, api, extraOptions);

      // Another await, another chance for the session to have been replaced. Without this the
      // retry's data reaches the replacement session, or its 401 signs that session out.
      if (!stillOwned()) {
        return createStaleSessionError();
      }

      if (result.error?.status === 401) {
        // Session truly expired — clear client-side state
        api.dispatch(clearUser());
        clearWorkOSCookies();
        window.location.href = withBasePath(PathName.Login);
      }
      return result;
    }

    if (!refreshToken && !guestUserRefreshToken) {
      // if this is orchestrate flow, we don't need to clear user, we need to regenerate the token. To do that we move user back so he can regenerate the token
      if (!!user?.accessToken) {
        window.history.back();
      } else {
        notifyToast('Session has expired, please log in again');
        api.dispatch(clearUser());
        window.location.href = withBasePath(PathName.Login);
      }
      return result;
    }

    // try to get a new token
    let pendingRefresh: Promise<IRes | null> | null = null;
    // Declared outside the try so `finally` releases the same key it claimed.
    const refreshTokenValue = refreshToken || guestUserRefreshToken;

    try {
      // Keyed like the quiet path, and for the same reason: one patient can replace another in this
      // document, so an unkeyed join would install the other patient's pair and retry this request
      // authenticated as them.
      pendingRefresh = joinOrStartRenewal(refreshTokenValue, () =>
        requestTokenRefresh(refreshTokenValue, api, extraOptions)
      );

      const refreshResult = await pendingRefresh;

      /**
       * Re-read the live session after the await. Keying the map stops one session JOINING
       * another's renewal, but it cannot stop this session becoming stale while its own renewal is
       * in flight: the portal replaces one patient with another in the same document, and this
       * refresh token still succeeds because nothing revokes it. Installing the result then
       * overwrites the replacement patient's tokens, and failing then signs them out.
       *
       * `returnedRefreshToken` keeps same-session deduplication working. When two callers join one
       * renewal, the first may already have installed the new token by the time the second resumes,
       * so the live value is the RETURNED token rather than the presented one. Both belong to the
       * same renewal lineage; anything else is a different session.
       */
      // Recorded before the check so a co-joining caller that already installed the new pair is
      // recognised as the same session rather than a replacement.
      recordRenewalLineage(refreshTokenValue, refreshResult?.data?.data?.refreshToken);

      if (!ownsSession(refreshTokenValue, sessionTokenOf(api.getState() as RootState))) {
        // Abandon without mutating a session this renewal was never about. Returning the original
        // 401 is correct: the caller's request belonged to the session that has gone.
        return result;
      }

      if (!refreshResult || refreshResult?.error) {
        api.dispatch(clearUser());
        throw new Error('Error while getting refresh token');
      }

      const { data } = refreshResult.data;

      if (data) {
        // if we are not logged in and have only guest access token
        if (guestUserRefreshToken) {
          api.dispatch(setUserState(data));
        } else {
          // else log user in
          api.dispatch(setToken(data));
        }

        // retry the initial query
        result = await baseQuery(args, api, extraOptions);

        // The last await. A replacement here would otherwise deliver this patient's retried result
        // into the session that took their place.
        if (!stillOwned()) {
          return createStaleSessionError();
        }
      } else {
        api.dispatch(clearUser());
      }
    } catch (e) {
      // log out user if we can't get a new token
      api.dispatch(clearUser());
      throw e;
    } finally {
      // Clear the entry after use to allow future refresh attempts
      releaseRenewal(refreshTokenValue, pendingRefresh);
    }
  }

  return result;
};

type PaginatedResponse<Item> = ResponseProps<Item[], unknown>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type FetchAllPagesOptions<Item, Response extends PaginatedResponse<Item>> = {
  api: BaseQueryApi;
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;
  extraOptions: object;
  limit?: number;
  pageNo?: number;
  params?: Record<string, unknown>;
  url: string;
};

const extractItemId = (item: unknown): string | undefined => {
  if (item && typeof item === 'object') {
    const record = item as Record<string, unknown>;
    if (typeof record._id === 'string') return record._id;
    if (typeof record.id === 'string') return record.id;
  }
  return undefined;
};

const extractTotalCount = (info: unknown): number | undefined => {
  if (info && typeof info === 'object' && 'totalCount' in info) {
    return (info as { totalCount?: number }).totalCount;
  }
  return undefined;
};

export const fetchAllPages = async <Item, Response extends PaginatedResponse<Item>>(
  options: FetchAllPagesOptions<Item, Response>
): Promise<{ data: Response } | { error: FetchBaseQueryError }> => {
  const { api, baseQuery, extraOptions, limit = 20, pageNo = 0, params, url } = options;

  const createEmptyResponse = (): Response =>
    ({
      data: [],
      errorCode: 0,
      info: {} as Response['info'],
      message: ''
    }) as unknown as Response;

  if (limit <= 0) {
    return { data: createEmptyResponse() };
  }

  const items: Item[] = [];
  const seenIds = new Set<string>();
  let currentPage = pageNo;
  let lastResponse: Response | undefined;
  let latestTotalCount: number | undefined;

  while (true) {
    const request: FetchArgs = { params: { ...params, limit, pageNo: currentPage }, url };

    const result = await baseQuery(request as string | FetchArgs, api, extraOptions);

    if (result.error) {
      return { error: result.error };
    }

    const payload = result.data as Response | undefined;
    if (!payload) break;

    lastResponse = payload;
    const pageItems = payload.data ?? [];
    const beforeLength = items.length;

    for (const item of pageItems) {
      const itemId = extractItemId(item);

      if (itemId) {
        if (!seenIds.has(itemId)) {
          seenIds.add(itemId);
          items.push(item);
        }
      } else {
        items.push(item);
      }
    }

    const totalFromInfo = extractTotalCount(payload.info);
    if (totalFromInfo !== undefined) {
      latestTotalCount = totalFromInfo;
    }

    const noNewItems = items.length === beforeLength;
    const reachedTotal = latestTotalCount !== undefined && items.length >= latestTotalCount;
    const isLastPage = !pageItems.length || pageItems.length < limit || reachedTotal || noNewItems;

    if (isLastPage) {
      break;
    }

    currentPage += 1;
  }

  const baseResponse = lastResponse ?? createEmptyResponse();
  const totalCount = Math.max(latestTotalCount ?? 0, items.length);

  const mergedInfo =
    baseResponse.info && typeof baseResponse.info === 'object'
      ? { ...(baseResponse.info as Record<string, unknown>), totalCount }
      : { totalCount };

  return {
    data: {
      ...baseResponse,
      data: items,
      info: mergedInfo as Response['info']
    }
  };
};
